import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AttendanceExportCategory, Lesson, Term } from '@court-sync/shared';
import { lessonService } from '../services/lesson';
import { termService } from '../services/term';

/** Lessons not tied to a term still appear when a term is selected (e.g. camps for attendance export). */
function isExportScopeNonTermLesson(lesson: Lesson): boolean {
  return lesson.lessonType === 'camp' || (lesson.lessonType === 'private' && !lesson.termId);
}

export function useAttendanceExportScope(academyId: string | undefined) {
  const [terms, setTerms] = useState<Term[]>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState(false);
  const [termId, setTermId] = useState('');
  const [lessonId, setLessonId] = useState('');
  const [category, setCategory] = useState<AttendanceExportCategory | ''>('');

  useEffect(() => {
    if (!academyId) {
      setTerms([]);
      setLessons([]);
      setTermId('');
      setLessonId('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [termsRes, lessonsRes] = await Promise.all([
          termService.getTerms(academyId),
          lessonService.getLessons({ academyId }),
        ]);
        if (cancelled) return;
        setTerms(termsRes.data ?? []);
        setLessons(lessonsRes.data ?? []);
      } catch {
        if (!cancelled) {
          setTerms([]);
          setLessons([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [academyId]);

  const setTermIdAndMaybeClearLesson = useCallback(
    (id: string) => {
      setTermId(id);
      setLessonId((prev) => {
        if (!prev) return prev;
        const selected = lessons.find((l) => l.id === prev);
        if (selected && isExportScopeNonTermLesson(selected)) {
          return prev;
        }
        const pool = id ? lessons.filter((l) => l.termId === id) : lessons;
        return pool.some((l) => l.id === prev) ? prev : '';
      });
    },
    [lessons]
  );

  const filteredLessons = useMemo(() => {
    if (!termId) return lessons;
    return lessons.filter((l) => l.termId === termId || isExportScopeNonTermLesson(l));
  }, [lessons, termId]);

  const scope = useMemo(
    () => ({
      termId: termId || undefined,
      lessonId: lessonId || undefined,
      category: category || undefined,
    }),
    [termId, lessonId, category]
  );

  return {
    terms,
    filteredLessons,
    termId,
    lessonId,
    category,
    setTermId: setTermIdAndMaybeClearLesson,
    setLessonId,
    setCategory,
    scope,
    loading,
  };
}
